import type { GlobalStorage } from "@otto-ec/global-resources";
import type { HistorySearch, HistorySearchItem } from "../types/suggestion";

import * as protobufHistory from "@otto-ec/squirrel_protobuf-history";
import { STORAGE_ITEM } from "../types/constants";
import { default as B } from "buffer";
import { CookieBanner } from "../helper";

export class SubmitHistory {
  private _searches: HistorySearchItem[];
  private readonly _maxTotalLength: number;
  private readonly _maxViewedLength: number;

  constructor(maxTotalLength: number, maxViewedLength: number) {
    this._searches = CookieBanner.isEnabled() ? this.readSearchesFromSessionStorage() : [];
    this._maxTotalLength = maxTotalLength;
    this._maxViewedLength = maxViewedLength > maxTotalLength ? maxTotalLength : maxViewedLength;
  }

  get maxTotalLength(): number {
    return this._maxTotalLength;
  }

  get maxViewedLength(): number {
    return this._maxViewedLength;
  }

  toSeconds(ms: number): number {
    if (ms <= 0) {
      return ms;
    }
    return Math.floor(ms / 1000);
  }

  encode(): string {
    const history: HistorySearch = protobufHistory.createHistory(
      this._searches.map((search: HistorySearchItem): protobufHistory.protoHistory.History.HistoryItem => {
        search.date = this.toSeconds(search.date);
        return search as protobufHistory.protoHistory.History.HistoryItem;
      }) as protobufHistory.protoHistory.History.HistoryItem[],
      this.toSeconds(Date.now()),
    );
    const binary: Uint8Array = history.serializeBinary();
    const buffer: B.Buffer = B.Buffer.from(binary);
    return buffer.toString("base64");
  }

  isEmpty(): boolean {
    return this.currentLength() <= 0;
  }

  retrieveAllSearches(): HistorySearchItem[] {
    return CookieBanner.isEnabled() ? this.readSearchesFromSessionStorage() : this._searches;
  }

  currentLength(): number {
    return this._searches.length;
  }

  retrieveViewedSearches(): HistorySearchItem[] {
    const totalPosition: number = this.maxTotalLength + 1;
    const currentPosition: number = this.currentLength() + 1;
    if (this.currentLength() >= this.maxTotalLength) {
      /*                                                   */
      const viewedPosition: number = this.maxTotalLength - this.maxViewedLength;
      return this._searches.slice(viewedPosition, totalPosition);
    } else if (this.currentLength() > this.maxViewedLength) {
      /*                                                                                 */
      const viewedPosition: number = this.currentLength() - this.maxViewedLength;
      return this._searches.slice(viewedPosition, currentPosition);
    } else {
      /*                                                       */
      return this._searches;
    }
  }

  addSearch(term: string) {
    if (term == "" || !term) {
      return;
    }
    const term_lower: string = term.toLowerCase();

    const date: number = new Date().getTime();
    const newItem: HistorySearchItem = new protobufHistory.protoHistory.History.HistoryItem({
      term: term_lower,
      date,
    });

    if (!this.isSearchIncluded(newItem)) {
      this._searches.push({ term: newItem.term, date: newItem.date });

      /*                                                                                      */
      if (this.currentLength() > this.maxTotalLength) {
        this._searches = this._searches.slice(1, this._maxTotalLength + 1);
      }
    } else {
      /*                                                                               */
      this.deleteSearch(this.getSearchPosition(newItem.term));
      this._searches.push({ term: newItem.term, date: newItem.date });
    }
    this.writeSearchesToSessionStorage();
  }

  deleteAllSearches() {
    const positions: number[] = [...this._searches.keys()];
    this.deleteSearches(positions);
  }

  deleteSearch(position: number) {
    this._searches = this._searches.filter((_, i: number) => i !== position);
    this.writeSearchesToSessionStorage();
  }

  deleteSearches(positions: number[]) {
    this._searches = this._searches.filter((_, i: number) => !positions.includes(i));
    this.writeSearchesToSessionStorage();
  }

  getSearchPosition(term: string): number {
    return this._searches.findIndex((search: HistorySearchItem): boolean => search.term === term);
  }

  private isSearchIncluded(newSearch: HistorySearchItem): boolean {
    return this._searches.some((search: HistorySearchItem): boolean => search.term === newSearch.term);
  }

  private static removeDuplicatesInHistory(historySearchItems: HistorySearchItem[]): HistorySearchItem[] {
    return [...historySearchItems]
      .reverse()
      .reduce((uniqueHistoryItems: HistorySearchItem[], item: HistorySearchItem) => {
        if (!uniqueHistoryItems.some((historyItem) => historyItem.term == item.term)) {
          uniqueHistoryItems.push(item);
        }
        return uniqueHistoryItems;
      }, [])
      .reverse();
  }

  private readSearchesFromSessionStorage(): HistorySearchItem[] {
    const storage: GlobalStorage = this.getStorage();
    const searchesString: string | null = storage.getItem(STORAGE_ITEM.HISTORICAL_DATA);
    const historySearchItems: HistorySearchItem[] = searchesString ? JSON.parse(searchesString) : [];
    return SubmitHistory.removeDuplicatesInHistory(historySearchItems);
  }

  private writeSearchesToSessionStorage() {
    if (CookieBanner.isEnabled()) {
      const storage: GlobalStorage = this.getStorage();
      const searchesString: string = JSON.stringify(this._searches);
      storage.setItem(STORAGE_ITEM.HISTORICAL_DATA, searchesString);
    }
  }

  getStorage(): GlobalStorage {
    return new window.o_global.Storage(() => window.localStorage);
  }
}

const instance: SubmitHistory = new SubmitHistory(10, 5);
export const submitHistory = {
  addSearch: instance.addSearch.bind(instance),
  encode: instance.encode.bind(instance),
  retrieveAllSearch: instance.retrieveAllSearches.bind(instance),
  retrieveViewedSearch: instance.retrieveViewedSearches.bind(instance),
  deleteAllSearches: instance.deleteAllSearches.bind(instance),
  deleteSearch: instance.deleteSearch.bind(instance),
  deleteSearches: instance.deleteSearches.bind(instance),
  isEmpty: instance.isEmpty.bind(instance),
  getSearchPosition: instance.getSearchPosition.bind(instance),
};
